/**
 * Assignment Table Component
 * 
 * Displays a table of jabatan assignments.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Edit, Trash2, UserCheck, Users, Building2, MapPin, ArrowUpDown } from "lucide-react";
import type { UserJabatanAssignment } from "@/services/managementService";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";

interface AssignmentTableProps {
  assignments: UserJabatanAssignment[];
  loading: boolean;
  onEdit?: (assignment: UserJabatanAssignment) => void;
  onDelete?: (assignment: UserJabatanAssignment) => void;
}

export function AssignmentTable({ assignments, loading, onEdit, onDelete }: AssignmentTableProps) {
  const [sortBy, setSortBy] = React.useState<"user" | "jabatan" | "createdAt">("createdAt");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");

  const getLevelLabel = (level: string) => {
    switch (level) {
      case "nasional":
        return "Nasional";
      case "province":
        return "Provinsi";
      case "city":
        return "Kota/Kabupaten";
      default:
        return level;
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case "nasional":
        return <Users className="h-3 w-3" />;
      case "province":
        return <Building2 className="h-3 w-3" />;
      case "city":
        return <MapPin className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "nasional":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 border-blue-200 dark:border-blue-800";
      case "province":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200 dark:border-green-800";
      case "city":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 border-purple-200 dark:border-purple-800";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 border-gray-200 dark:border-gray-800";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy", { locale: idLocale });
    } catch {
      return "-";
    }
  };

  // Sort assignments
  const sortedAssignments = React.useMemo(() => {
    const sorted = [...assignments];
    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (sortBy === "user") {
        aVal = (a.user?.nama_lengkap || "").toLowerCase();
        bVal = (b.user?.nama_lengkap || "").toLowerCase();
      } else if (sortBy === "jabatan") {
        aVal = a.jabatan.name.toLowerCase();
        bVal = b.jabatan.name.toLowerCase();
      } else {
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [assignments, sortBy, sortOrder]);

  const handleSort = (field: "user" | "jabatan" | "createdAt") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Daftar Penugasan
          </CardTitle>
          <CardDescription>
            Memuat data penugasan...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (assignments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Daftar Penugasan
          </CardTitle>
          <CardDescription>
            Total: 0 penugasan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-muted p-6">
                <UserCheck className="h-12 w-12 text-muted-foreground" />
              </div>
              <div>
                <p className="text-lg font-medium">Tidak ada data penugasan</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Mulai dengan menambahkan penugasan baru
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Daftar Penugasan
            </CardTitle>
            <CardDescription className="mt-1">
              Total: {assignments.length} penugasan
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border overflow-x-auto shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="min-w-[200px]">
                  <Button
                    variant="ghost"
                    className="h-8 px-2 hover:bg-transparent font-semibold"
                    onClick={() => handleSort("user")}
                  >
                    User
                    {sortBy === "user" && (
                      <ArrowUpDown className={cn("ml-2 h-3 w-3", sortOrder === "desc" && "rotate-180")} />
                    )}
                  </Button>
                </TableHead>
                <TableHead className="min-w-[180px]">
                  <Button
                    variant="ghost"
                    className="h-8 px-2 hover:bg-transparent font-semibold"
                    onClick={() => handleSort("jabatan")}
                  >
                    Jabatan
                    {sortBy === "jabatan" && (
                      <ArrowUpDown className={cn("ml-2 h-3 w-3", sortOrder === "desc" && "rotate-180")} />
                    )}
                  </Button>
                </TableHead>
                <TableHead className="min-w-[150px]">Lokasi</TableHead>
                <TableHead className="min-w-[120px]">Status</TableHead>
                <TableHead className="min-w-[150px]">
                  <Button
                    variant="ghost"
                    className="h-8 px-2 hover:bg-transparent font-semibold"
                    onClick={() => handleSort("createdAt")}
                  >
                    Tanggal Dibuat
                    {sortBy === "createdAt" && (
                      <ArrowUpDown className={cn("ml-2 h-3 w-3", sortOrder === "desc" && "rotate-180")} />
                    )}
                  </Button>
                </TableHead>
                {(onEdit || onDelete) && (
                  <TableHead className="min-w-[120px] text-center">Aksi</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedAssignments.map((assignment, index) => (
                <TableRow
                  key={assignment._id}
                  className={cn(
                    "hover:bg-muted/50 transition-colors",
                    index % 2 === 0 && "bg-background"
                  )}
                >
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {assignment.user ? (
                        <>
                          <span className="font-semibold text-sm">{assignment.user.nama_lengkap || "-"}</span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {assignment.user.callsign || "-"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {assignment.user.email || "-"}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">User tidak ditemukan</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-medium text-sm">{assignment.jabatan.name}</span>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium border w-fit",
                          getLevelColor(assignment.jabatan.level)
                        )}
                      >
                        {getLevelIcon(assignment.jabatan.level)}
                        {getLevelLabel(assignment.jabatan.level)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {assignment.province && (
                        <span className="text-sm">{assignment.province}</span>
                      )}
                      {assignment.city && (
                        <span className="text-xs text-muted-foreground">{assignment.city}</span>
                      )}
                      {!assignment.province && !assignment.city && (
                        <span className="text-xs text-muted-foreground italic">Tidak ada lokasi</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
                        assignment.isActive
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                      )}
                    >
                      {assignment.isActive ? "Aktif" : "Tidak Aktif"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">{formatDate(assignment.createdAt)}</span>
                      {assignment.startDate && (
                        <span className="text-xs text-muted-foreground">
                          Mulai: {formatDate(assignment.startDate)}
                        </span>
                      )}
                      {assignment.endDate && (
                        <span className="text-xs text-muted-foreground">
                          Selesai: {formatDate(assignment.endDate)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  {(onEdit || onDelete) && (
                    <TableCell>
                      <div className="flex items-center justify-center gap-2">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(assignment)}
                            className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                            title="Edit penugasan"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {onDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(assignment)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                            title="Hapus penugasan"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

